import { IntlShape } from 'react-intl'
import { Chart, ChartEvent, ChartOptions } from 'chart.js'
import { isNumber } from 'lodash-es'

import { CASE_ITEM_TYPE_TRANSLATION_IDS } from 'Constants/caseItemType'
import { CHART_COLORS } from 'Constants/charts'

import { OrdersReportCardChartMockData } from './OrdersReportCardChart.types'

export const generateOrdersReportCardChartDatasets = (
  data: OrdersReportCardChartMockData[],
  intl: IntlShape,
) =>
  data.map((item, i) => ({
    backgroundColor: 'rgba(0, 0, 0, 0.0)',
    borderColor: CHART_COLORS[i],
    data: item.data,
    fill: true,
    label: intl.formatMessage({
      id: CASE_ITEM_TYPE_TRANSLATION_IDS[item.type],
    }),
    pointBackgroundColor: CHART_COLORS[i],
  }))

export const generateOrdersReportCardChartLegendPlugin = (intl: IntlShape) => ({
  afterUpdate(chart: Chart<'line'>, args: any, options: ChartOptions<'line'>) {
    const legendContainer = document.getElementById(options.containerID!)

    let legendContainerList = legendContainer?.querySelector('ul')

    if (!legendContainerList) {
      legendContainerList = document.createElement('ul')
      legendContainerList.setAttribute(
        'class',
        'DashboardOrdersReportCard-legend-container',
      )

      legendContainer?.appendChild(legendContainerList)
    }

    while (legendContainerList.firstChild) {
      legendContainerList.firstChild.remove()
    }

    if (
      typeof chart.options.plugins?.legend?.labels?.generateLabels !==
      'function'
    ) {
      return
    }

    const legendItems =
      chart.options.plugins.legend.labels.generateLabels(chart)

    legendItems.forEach((item, index) => {
      if (typeof item.datasetIndex !== 'number') {
        return
      }

      // Create check box
      const checkbox = document.createElement('label')

      checkbox.setAttribute('class', 'Checkbox')

      const input = document.createElement('input')

      input.setAttribute('type', 'checkbox')

      if (chart.isDatasetVisible(item.datasetIndex)) {
        input.setAttribute('checked', '')
      }

      checkbox.appendChild(input)

      // Create legend item
      const legendItem = document.createElement('li')

      legendItem.setAttribute('class', 'DashboardOrdersReportCard-legend-item')

      legendItem.onclick = () => {
        chart.setDatasetVisibility(
          item.datasetIndex!,
          !chart.isDatasetVisible(item.datasetIndex!),
        )

        checkbox.removeAttribute('checked')

        chart.update(args)
      }

      // Create color box
      const boxSpan = document.createElement('span')

      boxSpan.style.background = item.strokeStyle?.toString() || ''
      boxSpan.style.outline = `${item.strokeStyle}80 solid 4px`

      boxSpan.setAttribute('class', 'DashboardOrdersReportCard-color-box')

      const detailsContainer = document.createElement('div')

      detailsContainer.setAttribute(
        'class',
        'DashboardOrdersReportCard-legend-details-container',
      )

      const nameContainer = document.createElement('p')

      nameContainer.setAttribute(
        'class',
        'DashboardOrdersReportCard-legend-name',
      )

      // Create name
      const name = document.createTextNode(`${item.text}`)

      nameContainer.appendChild(name)

      detailsContainer.appendChild(nameContainer)

      const totalContainer = document.createElement('p')

      totalContainer.setAttribute(
        'class',
        'DashboardOrdersReportCard-legend-total',
      )

      // Create total
      const total = document.createTextNode(
        `${intl.formatMessage(
          {
            id: 'Total_orders',
          },
          {
            total: chart.data.datasets[0].data[index]?.toString(),
          },
        )}`,
      )

      totalContainer.appendChild(total)

      detailsContainer.appendChild(totalContainer)

      // Append values to legend item and then append legend item to legend
      legendItem.appendChild(checkbox)
      legendItem.appendChild(boxSpan)
      legendItem.appendChild(detailsContainer)
      legendContainerList?.appendChild(legendItem)
    })
  },
  id: 'htmlLegend',
})

export const ordersReportCardChartMouseLinePlugin = {
  afterDraw(chart: Chart<'line'>) {
    const { ctx, chartArea } = chart
    const { bottom, top } = chartArea
    const { mouseLine } = chart.options

    if (!mouseLine) {
      return
    }

    const { x, color } = mouseLine

    if (isNumber(x)) {
      ctx.save()
      ctx.strokeStyle = color || ''
      ctx.lineWidth = 1
      ctx.moveTo(x, bottom)
      ctx.lineTo(x, top)
      ctx.stroke()
      ctx.restore()
    }
  },
  afterEvent(chart: Chart<'line'>, { event }: { event: ChartEvent }) {
    const { x, y } = event
    const { left, top, right, bottom } = chart.chartArea
    const { mouseLine } = chart.options

    if (!mouseLine || !x || !y) {
      return
    }

    if (
      x >= left &&
      y >= top &&
      x <= right &&
      y <= bottom &&
      chart.getActiveElements()[0].element
    ) {
      const { element } = chart.getActiveElements()[0]

      mouseLine.x = element.x
    } else {
      mouseLine.x = NaN
    }
  },
  id: 'mouseLine',
}
